import Modal from '@/components/modals/PopupModal'
import useIsMobile from '@/hooks/useIsMobile'
import { ButtonLabel, HeadingLabel, MetaDataLabel } from '@/styles/fonts'
import { GrandstandIcon } from '@grandstand-web/bally-web-shared/src/components/GrandstandIcon'
import { AnalyticServiceContext } from '@grandstand-web/bally-web-shared/src/services/analytics/AnalyticService'
import { DeviceResponse, UserServiceContext } from '@grandstand-web/bally-web-shared/src/services/user/UserService'
import { useRouter } from 'next/router'
import { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { GrandstandButton } from '../Buttons'

type ManageDevicesProps = {
  onBack: () => void
}

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
  overflow: auto;
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: var(--canvas);
  }

  ::-webkit-scrollbar-thumb {
    background: var(--on-background-alt);
  }
`

const ManageDevicesPartWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
`

const ManageDevicesHeading = styled(HeadingLabel)`
  color: var(--on-background);
  div {
    float: left;
    top: 3px;
    right: 10px;
    position: relative;
    @media (min-width: 768px) {
      display: none;
    }
  }
  div {
    float: left;
    top: 3px;
    right: 10px;
    position: relative;
    @media (min-width: 768px) {
      display: none;
    }
  }
`

const ManageDevicesDivider = styled.div`
  width: 100%;
  height: 0px;
  border-top: 1px solid var(--divider-canvas);
`

const ManageDevicesInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const DeviceControlHeading = styled(HeadingLabel)`
  color: var(--on-background);
`

const DeviceTypeLabel = styled(MetaDataLabel)`
  color: var(--on-background);
`

const DeviceDetailLabel = styled(MetaDataLabel)`
  color: var(--on-background-alt);
`

const ButtonText = styled(ButtonLabel)`
  text-transform: uppercase;
`

const ArrowIcon = styled(GrandstandIcon)``

const ManageDevices = ({ onBack }: ManageDevicesProps) => {
  const userService = useContext(UserServiceContext)
  const [showModal, setShowModal] = useState(false)
  const [otherDevices, setOtherDevices] = useState<DeviceResponse[]>([])
  const [currentDevice, setCurrentDevice] = useState<DeviceResponse>()
  const router = useRouter()
  const isMobile = useIsMobile()
  const analyticService = useContext(AnalyticServiceContext)

  const handleBackClick = () => {
    if (isMobile) {
      router.push('/settings')
      onBack()
    }
  }

  const handleShowModal = () => {
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
  }

  useEffect(() => {
    const getDevices = async () => {
      let _devices = await userService.getDevices()
      let _currentDevice = _devices.find((item) => item.is_current === true)
      let _otherDevices = _devices.filter((item) => item.is_current === false)
      setCurrentDevice(_currentDevice)
      setOtherDevices(_otherDevices)
    }
    getDevices()
  }, [userService])

  const handleRemoveAll = async () => {
    if (currentDevice) {
      let _devices = await userService.removeAllOtherDevices(currentDevice.id)
      let _currentDevice = _devices.find((item) => item.is_current === true)
      let _otherDevices = _devices.filter((item) => item.is_current === false)
      setCurrentDevice(_currentDevice)
      setOtherDevices(_otherDevices)
      analyticService.removeAllDevices()
    }
  }

  return (
    <MainContent>
      {showModal ? (
        <Modal show={showModal} onClose={() => setShowModal(false)}>
          <Modal.Title>Are you sure you want to remove all of your devices?</Modal.Title>
          <Modal.Subtitle>
            All of your devices will be signed out of FanDuel Sports Network except this device.
          </Modal.Subtitle>
          <Modal.Button buttonStyle="primary" onClick={handleRemoveAll}>
            Remove All
          </Modal.Button>
          <Modal.Button buttonStyle="alt" buttonSize="sm" onClick={handleCloseModal}>
            Keep All
          </Modal.Button>
        </Modal>
      ) : null}
      <ManageDevicesPartWrapper>
        <ManageDevicesHeading onClick={handleBackClick} fontSize="4">
          <ArrowIcon icon="arrow_left" />
          Manage Devices
        </ManageDevicesHeading>
      </ManageDevicesPartWrapper>
      <ManageDevicesDivider />

      <ManageDevicesPartWrapper>
        <ManageDevicesInfoWrapper>
          <DeviceControlHeading fontSize="0">Remove Registered Devices</DeviceControlHeading>
          <DeviceDetailLabel fontSize="sm">
            Click to remove all devices from accessing FanDuel Sports Network.
          </DeviceDetailLabel>
        </ManageDevicesInfoWrapper>
        <GrandstandButton buttonStyle="alt" buttonSize="xs" onClick={handleShowModal}>
          <ButtonText fontSize="xs">remove all</ButtonText>
        </GrandstandButton>
      </ManageDevicesPartWrapper>
      <ManageDevicesDivider />

      <ManageDevicesPartWrapper>
        <ManageDevicesInfoWrapper>
          <DeviceTypeLabel fontSize="sm">{currentDevice?.name}</DeviceTypeLabel>
          <DeviceDetailLabel fontSize="sm">Currently watching</DeviceDetailLabel>
        </ManageDevicesInfoWrapper>
      </ManageDevicesPartWrapper>
      <ManageDevicesDivider />

      {otherDevices.map((device, index) => [
        <ManageDevicesPartWrapper key={index}>
          <ManageDevicesInfoWrapper>
            <DeviceTypeLabel fontSize="sm">{device['name']}</DeviceTypeLabel>
          </ManageDevicesInfoWrapper>
        </ManageDevicesPartWrapper>,
        <ManageDevicesDivider key={`divider-${index}`} />,
      ])}
    </MainContent>
  )
}
export default ManageDevices
